import { useHookstate } from "@hookstate/core"
import React, { useEffect } from "react"
import CrudIndex from "~/components/CrudIndex"
import Layout from "~/components/Layout"
import { useLang } from "~/hooks/useLang"
import ViewMore from "../components/ViewMore"
import type { ActionButtonsProps } from "~/components/CrudIndex/interfaces/ICrudIndex"
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useHistory } from "react-router-dom"

type PeriodRangeState = {
  readonly period?: string | null
  readonly startDate?: string | null
  readonly endDate?: string | null
}

const getDefaultPeriodRange = (): PeriodRangeState => ({
  period: null,
  startDate: null,
  endDate: null,
})

// eslint-disable-next-line max-lines-per-function
export default function Index() {
  const { translate } = useLang()
  const history = useHistory()
  const warehouseId = useHookstate<number | null>(null)
  const equipmentId = useHookstate<number | null>(null)
  const materialId = useHookstate<number | null>(null)
  const swarehouseStatusId = useHookstate<number | null>(null)
  const receivedByUserId = useHookstate<number | null>(null)
  const onlyPrecautionsCreatedByMe = useHookstate<boolean>(false)
  const onlyReturnsCreatedByMe = useHookstate<boolean>(false)
  const warehouseByUser = useHookstate<boolean>(true)
  const forceRefresh = useHookstate(false)
  const periodRange = useHookstate<PeriodRangeState>(getDefaultPeriodRange())


  const extraActions = React.useMemo(() => {
    const actions: ActionButtonsProps[] = []

    // eslint-disable-next-line functional/immutable-data
    actions.push({
      iconButton: () => <KeyboardReturnIcon />,
      label: () => translate('Return'),
      action: (params) => { history.push(`/warehouse-checkout/return/${params.id}`) },
      showInMenu: function () {
        return false
      },
    })
    return actions
  }, [translate, history])

  useEffect(() => {
    document.title = translate('Resources - Dispatch System')
  }, [])

  return (
    <Layout marginLayout={true}>
      <CrudIndex
        model='warehouse-checkout'
        title='Checkout'
        extraActions={extraActions}
        editable={false}
        deleteable={false}
        showCreateButton={true}
        desableDelete={false}
        canView={true}
        defaultOrder={{ field: "checked_out_at", sort: "desc" }}
        customViewMoreComponent={ViewMore}
        rowActionDisabled={(row) => !row.hasAccess}
        viewTitle={'Checkout Details'}
        searchButton={() => forceRefresh.set(!forceRefresh.get())}
        forceRefresh={forceRefresh.get()}
        disableColumnsFilters
        exceptColumns={[
          'warehouse_id',
          'checked_out_by_user:id',
          'checked_out_by_user:registration',
          'checked_out_by_user:login',
          'checked_out_by_user_id',
          'received_by_user_id',
          'checked_in_by_user',
          'checked_in_by_user_id',
          'warehouse:agency',
          'is_active'
        ]}
        aliasLabel={[
          { column: 'warehouse:name', label: translate('Warehouse') },
          { column: 'status_label', label: translate('Status') },
          { column: 'checked_out_by_user:representation_name', label: translate('Officer in charge') },
          { column: 'received_by_user:representation_name', label: translate('User') },
          { column: 'checkout_equipments', label: translate('Equipments') },
          { column: 'checkout_materials', label: translate('Materials') },
          { column: 'checked_out_at', label: translate('Performed at') }
        ]}
        forceOrderColumns={[
          'warehouse:name',
          'status_label',
          'checked_out_by_user:representation_name',
          'received_by_user:representation_name',
          'checkout_equipments',
          'checkout_materials',
          'checked_out_at'
        ]}
        filters={[
          {
            name: 'warehouse_id',
            type: 'warehouse_id',
            variable: warehouseId,
            dependent: warehouseByUser,
            gridSize: 2.4,
          },
          {
            name: 'status',
            type: 'warehouseStatus_id',
            variable: swarehouseStatusId,
            gridSize: 2.4,
          },
          {
            name: 'equipment_id',
            type: 'equipment_id',
            dependent: warehouseId,
            variable: equipmentId,
            gridSize: 2.4,
          },
          {
            name: 'material_id',
            type: 'material_id',
            variable: materialId,
            dependent: warehouseId,
            gridSize: 2.4,
          },
          {
            name: 'received_by_user_id',
            type: 'user_id',
            variable: receivedByUserId,
            gridSize: 2.4,
          },
          {
            name: 'start_date',
            type: 'hidden',
            gridSize: 2.4,
            variable: periodRange.nested('startDate'),
          },
          {
            name: 'finish_date',
            type: 'hidden',
            gridSize: 2.4,
            variable: periodRange.nested('endDate'),
          },
          {
            type: 'period',
            placeholder: 'Período',
            variable: periodRange,
            gridSize: 9.6,
            sendToRequest: false,
          },
          {
            placeholder: 'Somente Cautelas criadas por mim',
            type: 'boolean_check',
            name: 'only_my_checkouts',
            variable: onlyPrecautionsCreatedByMe,
            gridSize: 3.2,
          },
          {
            placeholder: 'Somente Devoluções criadas por mim',
            type: 'boolean_check',
            name: 'only_my_checkins',
            variable: onlyReturnsCreatedByMe,
            gridSize: 3.2,
          },
        ]}
        datesField={['checked_out_at']}
        arrayField={[
          { field: 'checkout_equipments', value: 'equipment.name' },
          { field: 'checkout_materials', value: 'material.name' },
        ]}
        booleanField={['is_active']}
        showInViewMore={[
          'warehouse:name',
          'status_label',
          'is_active',
          'checked_out_by_user:representation_name',
          'received_by_user:representation_name',
          'checkout_equipments',
          'checkout_materials',
          'checked_out_at'
        ]}
        clearFilters={() => {
          warehouseId.set(null)
          swarehouseStatusId.set(null)
          equipmentId.set(null)
          materialId.set(null)
          receivedByUserId.set(null)
          periodRange.set(getDefaultPeriodRange())
          onlyPrecautionsCreatedByMe.set(false)
          onlyReturnsCreatedByMe.set(false)
          forceRefresh.set(!forceRefresh.get())
        }}

      />
    </Layout>
  )
}
